import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  applyNewWebsiteChannel,
  configureHostname,
  deleteWebsiteChannel,
  generatePayOrder,
  getClientHostedWebsites,
  getMyRdpChannels,
  modifyLocalWebServer,
  queryPayOrderStatus,
  requestOrRenewCert,
} from "../../../lib/api";
import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { GridMoreVertIcon } from "@mui/x-data-grid";
import { green, red, grey } from "@mui/material/colors";
import Grid from "@mui/material/Grid";
import HttpsIcon from "@mui/icons-material/Https";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

import { store } from "../../../redux/store";
import * as requestAction from "../../../redux/action/requestAction";
import { Bool } from "reselect/es/types";

/**
 * 网络通道客户端
 */
interface NetChannel {
  id: number;
  tag: string;
  expireTime: string;
  clientId: string;
  gatewayServer: string;
  gatewayPort: number;
  price: number;
  owner: string; // 其实是number类型，但是由于js number的精度不够，所以转成了字符串
}

interface WebSiteChannel {
  id: number;
  tag: string;
  clientId: string;
  localHttpIp: string;
  localHttpPort: number;
  certSupport: number;
  certExpireTime: string;
  subdomain: string;
  hostname: string;
  gatewayServer: string;
  createTime: string;
  certFile: string; // 证书文件
  keyFile: string; // 私钥文件
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  channel: NetChannel;
  websites: WebSiteChannel[];
  loadWebsiteChannels: (client: string) => void;
  payCallback: {
    setPayMessage: any;
    setPayStatus: any;
    setPayOpen: any;
  };
}
interface CardProps {
  channel: NetChannel;
  website?: WebSiteChannel;
  openDialog?: any;
}

interface WebSiteCardProps {
  channel: NetChannel;
  website: WebSiteChannel;
  loadWebsiteChannels: (clientId: string) => void;
  payCallback: {
    setPayMessage: any;
    setPayStatus: any;
    setPayOpen: any;
  };
}

interface AddWebSiteDialogProps {
  channel: NetChannel;
  open: boolean;
  closeDialog: () => void;
  loadWebsiteChannels: (clientId: string) => void;
}

/**
 * 自定义域名属性
 */
interface UpgradeSelfDomainProps {
  website: WebSiteChannel;
  visiable: boolean;
  loadWebsiteChannels: (clientId: string) => void;
  closeDialog: () => void;
}

function WebSiteCardToolbar(props: CardProps) {
  const { channel } = props;

  return (
    <Card sx={{ margin: 3 }}>
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: red[500] }} aria-label="recipe">
            包
          </Avatar>
        }
        title="工具包"
        subheader="辅助功能"
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary">
          为主机：{channel.tag} 下的web应用提供中继转发服务。
        </Typography>
      </CardContent>
      <CardActions>
        <Tooltip title="当成功申请了新的web站点通道后，需要重新生成配置，并将生成的配置文件：link.yml替换掉客户端里现有的配置，然后重启zhumi.link客户端，方能生效">
          <Button
            size="small"
            color="primary"
            onClick={() => {
              window.location.href = `${window.serverUrl}/rdp/config/generate/${channel.clientId}`;
            }}
          >
            生成配置文件
          </Button>
        </Tooltip>
        <Tooltip title="为您本地局域网的web服务（nginx,apache,tomcat,springboot）提供公网访问的能力（请不要滥用该功能，推荐开发调试和个人博客等场景使用）">
          <Button
            size="small"
            color="primary"
            onClick={() => {
              props.openDialog();
            }}
          >
            创建网站中继通道
          </Button>
        </Tooltip>

        <Tooltip title="查看证书生成命令">
          <Button
            size="small"
            color="primary"
            onClick={() => {
              window.open("https://service.joemeet.com/website/cert");
            }}
          >
            证书命令
          </Button>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

/**
 * 单个网站通道卡片
 * @param props
 * @returns
 */
function WebSiteCard(props: WebSiteCardProps) {
  const {
    website,
    loadWebsiteChannels,
    payCallback: { setPayMessage, setPayOpen, setPayStatus },
  } = props;
  const schema = website.certSupport === 0 ? "http" : "https";
  const price = 120;
  const bgcolor = website.certSupport === 0 ? grey[500] : green[500];

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [editMode, setEditMode] = React.useState(false);
  const [newIp, setNewIp] = React.useState(website.localHttpIp);
  const [newPort, setNewPort] = React.useState(website.localHttpPort);
  const [visiable, setVisiable] = React.useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  /**
   * 删除域名通道
   */
  async function handleDeleteWebsiteChannel() {
    const resp = await deleteWebsiteChannel(website.id);
    if (resp.success) {
      store.dispatch(requestAction.info(resp.status.describe));
      loadWebsiteChannels(website.clientId);
    } else {
      store.dispatch(requestAction.warning(resp.status.describe));
    }
    handleClose();
  }

  async function requestCert() {
    const channelId = website.id;
    const resp = await requestOrRenewCert(channelId);
    if (resp.success) {
      store.dispatch(requestAction.info(resp.status.describe));
      loadWebsiteChannels(website.clientId);
    } else {
      store.dispatch(requestAction.warning(resp.status.describe));
    }
  }

  /**
   * 请求支付
   */
  async function requestPay() {
    const domain = website.hostname
      ? website.hostname
      : `${website.subdomain}.${website.gatewayServer}`;
    const productName = `证书服务（${domain}）`;
    const orderType = "网站证书服务";
    const { data, headers } = await generatePayOrder(
      website.id as any,
      productName,
      orderType,
      price,
      12
    );
    console.log("headers=", headers);
    const outTradeNo = headers["outtradeno"];
    if (!outTradeNo) {
      store.dispatch(requestAction.warning("支付订单创建失败，请稍后再试"));
      return;
    }
    const newWindow = window.open("", "_blank");
    newWindow?.document.write(data);
    newWindow?.focus();

    setPayMessage("等待支付结果中...");
    setPayOpen(true);
    // 10s之后，每隔2s查询一次交易状态
    setTimeout(() => {
      const queryTask = setInterval(async () => {
        const kbResult = await queryPayOrderStatus(outTradeNo);
        if (!kbResult.success) {
          setPayMessage("");
          setPayOpen(false);
          store.dispatch(requestAction.warning(kbResult.status.describe));
          clearInterval(queryTask);
          return;
        }
        const { payStatus } = kbResult.response;
        setPayStatus(payStatus as string);
        if (payStatus === "success") {
          // 支付失败
          setPayMessage(`支付成功，感谢您的支持。`);
          setPayOpen(true);
          clearInterval(queryTask);
          return;
        }
        if (payStatus === "fail") {
          // 支付失败
          setPayMessage("支付失败");
          setPayOpen(true);
          clearInterval(queryTask);
          return;
        }
        if (payStatus === "init" || payStatus === "processing") {
          // 继续轮询支付结果
          setPayMessage("等待支付结果中...");
          setPayOpen(true);
        }
      }, 2000);
    }, 10 * 1000);
  }
  /**
   * 保存新的转发地址
   */
  async function saveNewTransfer() {
    const resp = await modifyLocalWebServer(website.id as any, newIp, newPort);
    if (resp.success) {
      setEditMode(false);
      loadWebsiteChannels(website.clientId);
      store.dispatch(
        requestAction.info("修改成功，请重新下载配置进行替换，并重启客户端")
      );
    } else {
      store.dispatch(requestAction.warning(resp.status.describe));
    }
  }
  return (
    <React.Fragment>
      <UpgradeSelfDomain
        loadWebsiteChannels={loadWebsiteChannels}
        website={website}
        visiable={visiable}
        closeDialog={() => {
          setVisiable(false);
        }}
      ></UpgradeSelfDomain>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <Tooltip title="请谨慎操作，没有二次确认框！删除后，如购买过证书，证书会被回收，如再申请需要重新购买！">
          <MenuItem
            onClick={handleDeleteWebsiteChannel}
            style={{ color: "red" }}
          >
            删除此域名通道
          </MenuItem>
        </Tooltip>
      </Menu>
      <Card sx={{ margin: 2 }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor }} aria-label="recipe">
              <HttpsIcon />
            </Avatar>
          }
          action={
            <IconButton aria-label="settings" onClick={handleClick}>
              <GridMoreVertIcon />
            </IconButton>
          }
          title={website.tag}
          subheader={website.hostname ? website.hostname : website.subdomain}
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            子域名地址：
            {
              <Link
                href={`${schema}://${website.subdomain}.${website.gatewayServer}`}
                target={"_blank"}
                variant="body2"
                style={{ textDecoration: "none" }}
              >{`${schema}://${website.subdomain}.${website.gatewayServer}`}</Link>
            }
          </Typography>
          {website.hostname && (
            <Typography variant="body2" color="text.secondary">
              自定义域名地址：
              {
                <Link
                  href={`${schema}://${website.hostname}`}
                  target={"_blank"}
                  variant="body2"
                  style={{ textDecoration: "none" }}
                >{`${schema}://${website.hostname}`}</Link>
              }
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            创建时间：{website.createTime}
          </Typography>
          {!editMode && (
            <Typography variant="body2" color="text.secondary">
              本地服务：{`${website.localHttpIp}:${website.localHttpPort}`}
              <Tooltip title="修改本地转发地址，需要重新生成配置，并替换配置后重启zhumi.link客户端">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditMode(true);
                  }}
                  style={{ marginLeft: 10 }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Typography>
          )}
          {editMode && (
            <Typography variant="body2" color="text.secondary">
              <Stack direction={"row"} style={{ margin: 20 }}>
                <TextField
                  defaultValue={website.localHttpIp}
                  label="本地IP地址"
                  variant="standard"
                  onChange={(event) => {
                    setNewIp(event.currentTarget.value);
                  }}
                />
                &nbsp;&nbsp;&nbsp;&nbsp;
                <TextField
                  defaultValue={website.localHttpPort}
                  label="端口号"
                  type="number"
                  variant="standard"
                  onChange={(event) => {
                    setNewPort(event.currentTarget.value as any);
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    saveNewTransfer();
                  }}
                  style={{ marginLeft: 10 }}
                >
                  <SaveIcon />
                </IconButton>
              </Stack>
            </Typography>
          )}

          {website.certSupport === 1 && (
            <Typography variant="body2" color="text.secondary">
              证书过期时间：{`${website.certExpireTime}`}
            </Typography>
          )}
          {website.certSupport === 2 && (
            <Chip label="证书申请中" color="primary" />
          )}
        </CardContent>
        <CardActions>
          {website.certSupport === 0 && (
            <Tooltip title="使用https访问您的网站使其更加安全。支持子域名，暂不支持自定义域名">
              <Button
                size="small"
                color="primary"
                onClick={() => {
                  requestCert();
                }}
              >
                申请安装证书
              </Button>
            </Tooltip>
          )}
          <Tooltip title="如果您有自己的域名，并且在阿里云已经备案，那么您可以升级配置自己的域名。">
            <Button
              size="small"
              color="primary"
              onClick={() => {
                setVisiable(true);
              }}
            >
              自定义域名设置
            </Button>
          </Tooltip>

          <Tooltip title="如果不希望任意人员都能访问,可以设置http basic认证">
            <Button
              size="small"
              color="primary"
              onClick={() => {
                store.dispatch(
                  requestAction.info("功能开发中，可以先手动更改配置文件")
                );
              }}
            >
              设置basic认证
            </Button>
          </Tooltip>
        </CardActions>
      </Card>
    </React.Fragment>
  );
}

function UpgradeSelfDomain(props: UpgradeSelfDomainProps) {
  const { website, visiable, closeDialog, loadWebsiteChannels } = props;
  const [hostname, setHostname] = React.useState<string>(website.hostname);
  const configHostname = async () => {
    const resp = await configureHostname(website.id, hostname);
    if (resp.success) {
      loadWebsiteChannels(website.clientId);
      closeDialog();
      store.dispatch(
        requestAction.info("自定义域名设置成功，请及时更新客户端配置")
      );
    } else {
      store.dispatch(requestAction.info(resp.status.describe));
    }
  };

  return (
    <Dialog open={visiable} onClose={closeDialog}>
      {
        // 不要将props.open 放到state里再使用，应该直接使用props.open。否则react感知不到props的变化
      }
      <DialogTitle>配置自定义域名</DialogTitle>
      <DialogContent>
        <DialogContentText variant="body2">
          您的域名需要在阿里云成功备案。
        </DialogContentText>
        <DialogContentText variant="body2">
          请勿滥用；如您不会配置DNS：右下角，联系管理员。
        </DialogContentText>
        <DialogContentText variant="body2">
          自定义域名如需https，需提供自定义域名的数字证书（密钥和公钥都需要）
        </DialogContentText>
        <TextField
          margin="dense"
          name="id"
          id="id"
          label="网站通道标识符"
          fullWidth
          value={website.id}
          variant="filled"
          style={{ display: "none" }}
        />
        <TextField
          autoFocus
          margin="dense"
          name="hostname"
          id="hostname"
          autoComplete="off"
          fullWidth
          onChange={(e) => {
            setHostname(e.currentTarget.value);
          }}
          label="您的域名（请配置好CNAME）"
          value={hostname}
          placeholder="在阿里云成功备案的域名"
          variant="filled"
        />
        <DialogContentText variant="body2" color={"secondary"}>
          保存之前请配置您的DNS:
        </DialogContentText>
        <DialogContentText variant="body2" color={"secondary"}>
          新增CNAME记录 {hostname ? hostname : "xxx.abc.com"} 指向{" "}
          {website.subdomain + "." + website.gatewayServer}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>取消</Button>
        <Button
          onClick={() => {
            configHostname();
          }}
          disabled={!hostname || hostname === website.hostname}
        >
          保存/修改
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * 添加web站点对话框
 * @param props
 * @returns
 */
function AddWebSiteDialog(props: AddWebSiteDialogProps) {
  const { loadWebsiteChannels, channel, open, closeDialog } = props;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const resp = await applyNewWebsiteChannel(data);
    if (resp.success) {
      loadWebsiteChannels(channel.clientId);
      store.dispatch(requestAction.info(resp.status.describe));
      closeDialog();
    } else {
      store.dispatch(requestAction.warning(resp.status.describe));
    }
  };
  return (
    <Dialog open={open} onClose={closeDialog}>
      {
        // 不要将props.open 放到state里再使用，应该直接使用props.open。否则react感知不到props的变化
      }
      <DialogTitle>新建web站点中继通道</DialogTitle>
      <DialogContent>
        <DialogContentText variant="body2">
          您将创建一个新的网站通道。意味着你的本地服务将可以被公网访问。
        </DialogContentText>
        <DialogContentText variant="body2">
          请勿滥用网络通道提供非法的内容访问。
        </DialogContentText>
        <DialogContentText variant="body2">
          建议使用在开发测试，个人博客等场景。
        </DialogContentText>
      </DialogContent>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
        <DialogContent>
          <TextField
            margin="dense"
            name="clientId"
            id="clientId"
            label="客户端ID"
            fullWidth
            defaultValue={channel.clientId}
            variant="filled"
            style={{ display: "none" }}
          />
          <TextField
            autoFocus
            margin="dense"
            name="tag"
            id="tag"
            label="网站名称"
            autoComplete="off"
            placeholder="随意设置，主要是方便您自己管理"
            fullWidth
            variant="filled"
          />
          <TextField
            margin="dense"
            name="subdomain"
            id="subdomain"
            autoComplete="off"
            label="子域名（创建后可升级自定义域名）"
            placeholder="通过该子域名访问您的内网web服务"
            variant="filled"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  .{channel.gatewayServer}
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            autoComplete="off"
            name="localHttpIp"
            id="localHttpIp"
            label="本地Web服务IP地址"
            placeholder="Web服务器所在电脑的内网（局域网）IP地址"
            fullWidth
            variant="filled"
          />
          <TextField
            margin="dense"
            name="localHttpPort"
            autoComplete="off"
            id="localHttpPort"
            label="本地Web服务端口号"
            placeholder="内网（局域网）Web服务器的端口号"
            fullWidth
            variant="filled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>取消</Button>
          <Button type="submit">创建</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function TabPanel(props: TabPanelProps) {
  const { payCallback, loadWebsiteChannels, websites, channel, value, index } =
    props;
  const [open, setOpen] = React.useState(false);

  function openDialog() {
    setOpen(true);
  }
  function closeDialog() {
    setOpen(false);
  }
  const rows = (websites.length + 1) / 4 + 1;
  const height = rows * 180; // 高度需要动态计算，否则元素少的时候，会导致元素之间间距过大，grid布局需要平分空间
  return (
    <React.Fragment>
      <AddWebSiteDialog
        loadWebsiteChannels={loadWebsiteChannels}
        closeDialog={closeDialog}
        open={open}
        channel={channel}
      ></AddWebSiteDialog>
      {value === index && (
        <Grid container style={{ height: height }} direction="row">
          <Grid item xs={4}>
            <WebSiteCardToolbar openDialog={openDialog} channel={channel} />
          </Grid>
          {websites &&
            websites.length > 0 &&
            websites.map((website, index) => (
              <Grid item xs={4} key={index}>
                <WebSiteCard
                  payCallback={payCallback}
                  loadWebsiteChannels={loadWebsiteChannels}
                  website={website}
                  channel={channel}
                />
              </Grid>
            ))}
        </Grid>
      )}
    </React.Fragment>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

export function WebSites() {
  const [value, setValue] = React.useState(0);
  const [tabs, setTabs] = React.useState<any[]>([]);
  const [payOpen, setPayOpen] = React.useState(false);
  const [payStatus, setPayStatus] = React.useState("init");
  const [webChannelList, setWebChannelList] = React.useState<any[]>([]);
  const [payMessage, setPayMessage] = React.useState("");
  /**
   * 切换客户端时触发
   * @param event
   * @param newValue
   */
  const handleChange = async (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setValue(newValue);
    const tab = tabs[newValue] as any;
    loadWebsiteChannels(tab.clientId);
  };

  // 查询我的所有客户端
  React.useEffect(() => {
    getMyRdpChannels().then(async (res) => {
      const list = res.response.list
        ? res.response.list.filter((value) => value.tag)
        : [];
      setTabs(list);
      if (list.length >= 1) {
        // 查询第一个
        const tab = list[0];
        loadWebsiteChannels(tab.clientId);
      }

      if (res.response.list && res.response.list.length - list.length !== 0) {
        store.dispatch(
          requestAction.warning(
            `有${
              res.response.list.length - list.length
            }个桌面通道由于您没有设置标签，没有展示出来！`
          )
        );
      }
    });
  }, []); // 传入空数组，相当于didMount，挂载成功时请求一次
  /**
   * 载入和客户端绑定的所有web服务
   * @param clientId
   */
  async function loadWebsiteChannels(clientId: string) {
    const resp = await getClientHostedWebsites(clientId);
    resp.response.list && setWebChannelList(resp.response.list);
  }

  async function closePayModal() {
    const tab = tabs[value] as any;
    loadWebsiteChannels(tab.clientId);
    setPayOpen(false);
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        bgcolor: "rgb(245, 245, 245)",
        display: "flex",
        height: window.innerHeight - 64,
      }}
    >
      <Modal
        open={payOpen}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Container sx={style}>
          {(payStatus === "init" || payStatus === "processing") && (
            <CircularProgress />
          )}
          <Typography
            variant="body2"
            id="modal-modal-description"
            style={{ marginBottom: 20 }}
            sx={{ mt: 2 }}
          >
            {payMessage}
          </Typography>
          <Stack direction="row" spacing={2}>
            {(payStatus === "success" || payStatus === "fail") && (
              <Button
                variant="outlined"
                color="success"
                onClick={() => {
                  closePayModal();
                }}
              >
                关闭
              </Button>
            )}
          </Stack>
        </Container>
      </Modal>
      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={value}
        textColor="secondary"
        indicatorColor="secondary"
        onChange={handleChange}
        sx={{
          borderRight: 1,
          minWidth: 210,
          borderColor: "divider",
          alignItems: "start",
        }}
      >
        {tabs.map((tab, index) => {
          return (
            <Tab
              sx={{ minWidth: 210, alignItems: "start", textTransform: "none" }}
              key={index}
              label={(tab as any).tag}
              {...a11yProps(index)}
            />
          );
        })}
      </Tabs>

      {tabs.map((tab, index) => {
        return (
          <TabPanel
            payCallback={{ setPayOpen, setPayMessage, setPayStatus }}
            loadWebsiteChannels={loadWebsiteChannels}
            key={index}
            value={value}
            channel={tab}
            websites={webChannelList}
            index={index}
          />
        );
      })}
    </Box>
  );
}

const style = {
  position: "absolute" as "absolute",
  top: "30%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  justifyContent: "center",
  alignItems: "center",
};
